import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// Stars scattered across the hero — sized in vw so they scale with the screen
const STARS = [
  // original spread
  { id: 1,  img: 'star1', top: '8%',  left: '13%', size: 24, dur: 8,  del: 0,   mx: -22, my: -13 },
  { id: 2,  img: 'star2', top: '18%', left: '37%', size: 19, dur: 11, del: 2,   mx: -16, my: -9  },
  { id: 3,  img: 'star4', top: '6%',  left: '57%', size: 17, dur: 9,  del: 1,   mx: -20, my: -11 },
  { id: 4,  img: 'star3', top: '33%', left: '22%', size: 22, dur: 7,  del: 0.5, mx: -27, my: -17 },
  { id: 5,  img: 'star1', top: '52%', left: '67%', size: 25, dur: 10, del: 3,   mx: -13, my: -7  },
  { id: 6,  img: 'star2', top: '65%', left: '28%', size: 18, dur: 12, del: 1.5, mx: -19, my: -11 },
  { id: 7,  img: 'star4', top: '13%', left: '79%', size: 20, dur: 8,  del: 4,   mx: -11, my: -6  },
  { id: 8,  img: 'star3', top: '44%', left: '47%', size: 16, dur: 9,  del: 2.5, mx: -24, my: -15 },
  { id: 9,  img: 'star1', top: '78%', left: '53%', size: 27, dur: 11, del: 0,   mx: -14, my: -8  },
  { id: 10, img: 'star2', top: '32%', left: '4%',  size: 19, dur: 7,  del: 3.5, mx: -23, my: -14 },
  { id: 11, img: 'star4', top: '59%', left: '13%', size: 17, dur: 10, del: 1,   mx: -18, my: -10 },
  { id: 12, img: 'star3', top: '87%', left: '73%', size: 21, dur: 13, del: 2,   mx: -12, my: -7  },
  { id: 13, img: 'star1', top: '40%', left: '87%', size: 18, dur: 8,  del: 5,   mx: -9,  my: -5  },
  { id: 14, img: 'star2', top: '72%', left: '43%', size: 23, dur: 9,  del: 0.8, mx: -16, my: -9  },
  { id: 15, img: 'star4', top: '3%',  left: '32%', size: 16, dur: 10, del: 3.2, mx: -21, my: -12 },
  // extra stars to fill the scene on wide screens
  { id: 16, img: 'star2', top: '15%', left: '50%', size: 20, dur: 9,  del: 1.8, mx: -16, my: -10 },
  { id: 17, img: 'star1', top: '36%', left: '74%', size: 15, dur: 10, del: 0.6, mx: -11, my: -6  },
  { id: 18, img: 'star3', top: '68%', left: '18%', size: 19, dur: 8,  del: 2.8, mx: -17, my: -10 },
  { id: 19, img: 'star4', top: '26%', left: '62%', size: 14, dur: 11, del: 4.2, mx: -15, my: -8  },
  { id: 20, img: 'star1', top: '56%', left: '84%', size: 22, dur: 9,  del: 1.3, mx: -9,  my: -5  },
  { id: 21, img: 'star2', top: '82%', left: '30%', size: 17, dur: 12, del: 3.7, mx: -14, my: -8  },
  { id: 22, img: 'star4', top: '47%', left: '8%',  size: 15, dur: 8,  del: 5.5, mx: -19, my: -11 },
  { id: 23, img: 'star3', top: '91%', left: '55%', size: 20, dur: 10, del: 0.3, mx: -13, my: -7  },
];

function SpaceHero() {
  const heroRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [twinkling, setTwinkling] = useState(false);

  function handleMouseMove(e) {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMouse({
      x: ((e.clientX - rect.left)  / rect.width  - 0.5) * 2, // -1 to 1
      y: ((e.clientY - rect.top)   / rect.height - 0.5) * 2,
    });
  }

  function handleMouseLeave() {
    // Smoothly return to center
    setMouse({ x: 0, y: 0 });
  }

  // Click anywhere in hero: trigger a brief star twinkle pulse
  function handleClick() {
    if (twinkling) return;
    setTwinkling(true);
    setTimeout(() => setTwinkling(false), 800);
  }

  // Inline style for parallax — shifts element opposite to cursor (depth illusion)
  function parallax(mx, my) {
    return {
      transform: `translate(${mouse.x * mx}px, ${mouse.y * my}px)`,
      transition: 'transform 0.14s ease-out',
    };
  }

  return (
    <>
      {/* All keyframe animations scoped to the hero */}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-13px); }
        }
        @keyframes heroFloatSlow {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-8px); }
        }
        /*
          10-point ellipse: center (52vw, 41vh), rx 16vw, ry 20vh.
          Stays in the open center of the hero, clear of all planet corners.
          70s linear = constant, peaceful drift — never speeds up or slows down.
        */
        @keyframes pigDrift {
          0%   { transform: translate(52vw, 21vh) rotate(-4deg); }
          10%  { transform: translate(43vw, 25vh) rotate(-8deg); }
          20%  { transform: translate(37vw, 35vh) rotate(-7deg); }
          30%  { transform: translate(37vw, 47vh) rotate(-3deg); }
          40%  { transform: translate(43vw, 57vh) rotate(3deg);  }
          50%  { transform: translate(52vw, 61vh) rotate(7deg);  }
          60%  { transform: translate(61vw, 57vh) rotate(8deg);  }
          70%  { transform: translate(67vw, 47vh) rotate(5deg);  }
          80%  { transform: translate(67vw, 35vh) rotate(-1deg); }
          90%  { transform: translate(61vw, 25vh) rotate(-5deg); }
          100% { transform: translate(52vw, 21vh) rotate(-4deg); }
        }
        @keyframes scrollBounce {
          0%, 100% { opacity: 0.35; transform: translateX(-50%) translateY(0px);  }
          50%       { opacity: 0.7;  transform: translateX(-50%) translateY(7px); }
        }
        /*
          Rocket movement only — no rotate here.
          The <img> inside gets a static rotate so the two transforms never conflict.
          Starts at bottom-center, exits upper-right.
        */
        @keyframes rocketFly {
          0%   { transform: translate(0px, 0px);      opacity: 0; }
          6%   { transform: translate(3vw, -50px);    opacity: 0.9; }
          94%  { transform: translate(37vw, -530px);  opacity: 0.9; }
          100% { transform: translate(40vw, -600px);  opacity: 0; }
        }
        @keyframes starDrift {
          0%, 100% { transform: translate(0px, 0px);  opacity: 0.75; }
          33%       { transform: translate(5px, -7px); opacity: 1;    }
          66%       { transform: translate(-4px, 5px); opacity: 0.6;  }
        }
        @keyframes starPulse {
          0%   { transform: scale(1);   opacity: 0.75; filter: brightness(1); }
          50%  { transform: scale(2.4); opacity: 1;    filter: brightness(3.5) drop-shadow(0 0 6px white); }
          100% { transform: scale(1);   opacity: 0.75; filter: brightness(1); }
        }
      `}</style>

      <div
        ref={heroRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{
          position: 'relative',
          background: '#000',
          overflow: 'hidden',
          minHeight: 'calc(100vh - var(--navbar-h))',
          userSelect: 'none',
          cursor: 'default',
        }}
      >

        {/* ── Stars — bottom layer (z=1), everything renders on top of these ── */}
        {STARS.map(star => (
          <div
            key={star.id}
            style={{
              position: 'absolute',
              top: star.top,
              left: star.left,
              zIndex: 1,
              ...parallax(star.mx, star.my),
            }}
          >
            <img
              src={`/images/space/${star.img}.png`}
              alt=""
              draggable="false"
              style={{
                width: `${star.size}px`,
                display: 'block',
                animation: twinkling
                  ? `starPulse 0.7s ease-in-out`
                  : `starDrift ${star.dur}s ease-in-out ${star.del}s infinite`,
              }}
            />
          </div>
        ))}

        {/*
          ── Rocket — z=2, above stars but below all planets.
             3-level structure keeps transforms from conflicting:
               outer div  → absolute position + parallax  (parallax transform)
               middle div → rocketFly animation            (translate + opacity)
               inner img  → static rotate                  (visual tilt only)
             Starts bottom-center, exits upper-right diagonally.
        ── */}
        {/* Rocket — starts bottom ~35% from left, flies toward top-right, no rotation applied */}
        <div style={{ position: 'absolute', bottom: 0, left: '35%', zIndex: 2, ...parallax(-9, -5) }}>
          <div style={{ animation: 'rocketFly 22s ease-in 4s infinite' }}>
            <img
              src="/images/space/rocket.png"
              alt=""
              draggable="false"
              style={{ height: '70px', width: 'auto', display: 'block' }}
            />
          </div>
        </div>

        {/* ── Pig planet — bottom left, shifted slightly right ── */}
        <div style={{ position: 'absolute', bottom: '30px', left: '50px', zIndex: 3, ...parallax(-10, -6) }}>
          <img
            src="/images/space/pig-planet.png"
            alt=""
            draggable="false"
            style={{ width: '22vw', minWidth: '200px', display: 'block', animation: 'heroFloatSlow 13s ease-in-out infinite 3s' }}
          />
        </div>

        {/* ── Big planet — bottom right, nudged up so more is visible ── */}
        <div style={{ position: 'absolute', bottom: '-30px', right: '-60px', zIndex: 3, ...parallax(-9, -5) }}>
          <img
            src="/images/space/planet-big.png"
            alt=""
            draggable="false"
            style={{ width: '26vw', minWidth: '260px', display: 'block', animation: 'heroFloatSlow 11s ease-in-out infinite' }}
          />
        </div>

        {/* ── Saturn — top right, z=3 ── */}
        <div style={{ position: 'absolute', top: '3%', right: '4%', zIndex: 3, ...parallax(-14, -7) }}>
          <img
            src="/images/space/planet-saturn.png"
            alt=""
            draggable="false"
            style={{ width: '20vw', minWidth: '180px', display: 'block', animation: 'heroFloat 7s ease-in-out infinite' }}
          />
        </div>

        {/* ── Moon — left side, z=3 ── */}
        <div style={{ position: 'absolute', top: '13%', left: '4%', zIndex: 3, ...parallax(-19, -11) }}>
          <img
            src="/images/space/moon.png"
            alt=""
            draggable="false"
            style={{ width: '120px', display: 'block', animation: 'heroFloat 9s ease-in-out infinite 1.5s' }}
          />
        </div>

        {/* ── Pig astronaut — slow peaceful CSS drift, z=999 above everything ── */}
        <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 999, ...parallax(-7, -4) }}>
          <img
            src="/images/space/pig-astronaut.png"
            alt=""
            draggable="false"
            style={{ width: '170px', display: 'block', animation: 'pigDrift 70s linear infinite' }}
          />
        </div>

        {/* ── Scroll indicator — clickable arrow, scrolls past the hero ── */}
        <button
          onClick={e => {
            e.stopPropagation();
            const target = heroRef.current.getBoundingClientRect().bottom + window.scrollY;
            const start = window.scrollY;
            const distance = target - start;
            const duration = 700;
            const startTime = performance.now();
            function step(now) {
              const elapsed = now - startTime;
              const t = Math.min(elapsed / duration, 1);
              // easeInOutCubic
              const ease = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2;
              window.scrollTo(0, start + distance * ease);
              if (t < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
          }}
          style={{
            position: 'absolute',
            bottom: '28px',
            left: '50%',
            zIndex: 10,
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            animation: 'scrollBounce 2.4s ease-in-out infinite',
          }}
          aria-label="Scroll down"
        >
          <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1 L9 18 M2 12 L9 20 L16 12" stroke="rgba(255,255,255,0.65)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* ── Hero text — sits on top of everything ── */}
        <div style={{ position: 'relative', zIndex: 10, padding: '185px 0 120px' }}>
          <div className="container">
            {/* Offset text slightly right of the container's left edge */}
            <div style={{ marginLeft: '6%' }}>
              <p
                className="mb-2 text-uppercase"
                style={{ letterSpacing: '0.18em', color: '#c84b11', fontWeight: 600, fontSize: '0.85rem' }}
              >
                Est. 2026 &mdash; Pig-Inspired Apparel
              </p>
              <h1
                className="display-1 fw-bold mb-3"
                style={{ letterSpacing: '0.06em', lineHeight: 1.05, color: 'white' }}
              >
                PORK CO.
              </h1>
              <p className="mb-4" style={{ color: '#aaa', maxWidth: '480px', fontSize: '1.2rem' }}>
                Premium pig-inspired streetwear and accessories. Original designs.
              </p>
              <Link
                to="/products"
                className="btn btn-piggy btn-lg px-5 py-3"
                style={{ fontSize: '1rem', letterSpacing: '0.06em' }}
                onClick={e => e.stopPropagation()}
              >
                Explore the Collection
              </Link>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default SpaceHero;
